import type { ParsedUrlQuery } from 'querystring';
import { Base64 } from 'js-base64';

import { logger } from '@packages/utilities';
import type { SearchUserInteraction } from '@packages/gql/generated/shopping/graphql';
import type { SearchApiSSuggestParameter } from './api/rest/types';
import type {
  FilterRangeValue,
  SelectedFilterValue,
  DesktopDisplayMode,
  PagingMode,
} from './types';

export type CompressedSelectedFilterValues = {
  [name: string]: string[];
};

/**
 * Get the currently selected filter values from the url query
 *
 * @param query the current url query, from router or next context
 * @returns Selected filter values in a compressed object format
 */
export const decodeSelectedFilterValues = (
  query: ParsedUrlQuery,
): CompressedSelectedFilterValues => {
  try {
    return typeof query.f === 'string'
      ? JSON.parse(Base64.decode(decodeURIComponent(query.f)))
      : {};
  } catch (error) {
    logger.debug({ error }, 'cannot decode filter query');
  }
  return {};
};

// TODO unit?
const serializeRange = (value: FilterRangeValue) =>
  `${value.range[0] ?? ''}-${value.range[1] ?? ''}`;

const compressSelectedFilterValue = (value: SelectedFilterValue) =>
  value.selectedValues.concat(value.selectedRange ? serializeRange(value.selectedRange) : []);

/**
 * Convert selected filter values from a detailed and useful representation into a compressed form suitable for persistence in the url query
 *
 * @param values Selected filter values in expanded representation
 * @returns selected filter values in a compressed object form, ids only
 */
export const compressSelectedFilterValues = (
  values: SelectedFilterValue[],
): CompressedSelectedFilterValues =>
  Object.fromEntries(
    values
      .filter((value) => value.selectedRange || value.selectedValues.length)
      .map((value) => [value.filterId, compressSelectedFilterValue(value)]),
  );

/**
 * Encode the currently selected filter values to base64, into the correct url query parameter
 *
 * @param values Selected filter values in compressed representation
 * @returns Partial url query, to be merged into the full query
 */
export const encodeSelectedFilterValues = (values: CompressedSelectedFilterValues) => {
  const stringified = JSON.stringify(values);

  return stringified === '{}' ? { f: undefined } : { f: Base64.encodeURI(stringified) };
};

export const extractSelectedFilterValues = (query: ParsedUrlQuery): ParsedUrlQuery =>
  typeof query.f === 'string' ? { f: query.f } : {};

/**
 * Extract the current page number from the url query
 *
 * @param query Current url query, from router or Next SSR context
 * @returns Parsed page number, 1-based
 */

export const decodePage = (query: ParsedUrlQuery) => {
  // check if page number is a number string, else undefined to go to first page
  const currentPage =
    typeof query.p === 'string' && !Number.isNaN(parseInt(query.p, 10))
      ? parseInt(query.p, 10)
      : undefined;

  const pagingMode: PagingMode = currentPage === 0 ? 'infinite' : 'limited';

  return { pageSize: 72, currentPage, pagingMode };
};

/**
 * Encode a desired page number into the url query
 *
 * @param page Desired page number, 1-based
 * @returns Partial url query, to be merged into the full query
 */
export const encodePage = (page?: number) => ({
  p: page !== undefined ? page.toString(10) : page,
});

/**
 * Extract the current sorting order key
 *
 * @param query Current url query, from router or Next SSR context
 * @returns Sorting key
 */
export const decodeOrder = (query: ParsedUrlQuery) =>
  typeof query.o === 'string' ? query.o : undefined;

/**
 * Encode a desired sorting order key into the url query
 *
 * @param order Sorting order key
 * @returns Partial url query, to be merged into the full query
 */
export const encodeOrder = (order: string | undefined) => ({ o: order });

export const extractOrder = (query: ParsedUrlQuery): ParsedUrlQuery =>
  typeof query.o === 'string' ? { o: query.o } : {};

/**
 * Extract the category id to be used as a special category filter for search result pages, separate from the other filters
 *
 * @param query Current url query, from router or Next SSR context
 * @returns Category id (numeric id)
 */
export const decodeCategoryFilter = (query: ParsedUrlQuery) =>
  typeof query.c === 'string' ? query.c : undefined;

/**
 * Encode a category id as a special category filter used for search result pages, separate from the other filters
 *
 * @param categoryId Category id (numeric id)
 * @returns Partial url query, to be merged into the full query
 */
export const encodeCategoryFilter = (categoryId?: string) => ({ c: categoryId });

/**
 * Extract the current display mode value
 *
 * @param query Current url query, from router or Next SSR context
 * @returns display type value
 */
export const decodeDisplayMode = (query: ParsedUrlQuery): DesktopDisplayMode | undefined =>
  query.d === 'cards' || query.d === 'largeCards' || query.d === 'moreInformation'
    ? query.d
    : undefined;

/**
 * Encode a desired display mode value key into the url query
 *
 * @param displayValue display mode value
 * @returns Partial url query, to be merged into the full query
 */
export const encodeDisplayMode = (displayMode: DesktopDisplayMode | undefined) => ({
  d: displayMode,
});

export const extractDisplayMode = (query: ParsedUrlQuery): ParsedUrlQuery =>
  typeof query.d === 'string' ? { d: query.d } : {};

// NOTE: Consider Internal change to `q` instead of `searchquery` for consistency with other parameters
/**
 * Extract the current sorting order key
 *
 * @param query Current url query, from router or Next SSR context
 * @returns Sorting key
 */
export const decodeSearchQuery = (query: ParsedUrlQuery) =>
  typeof query.searchquery === 'string' ? query.searchquery.replace(/\+/g, ' ') : undefined;

/**
 * Encode a new search query into the url query
 *
 * @param searchquery New search query
 * @returns Partial url query, to be merged into the full query
 */
export const encodeSearchQuery = (searchquery: string) => ({ searchquery });

/**
 * Encode a user interaction that leads to a search page into the url query
 */
export const encodeUserInteraction = (userInteraction: SearchUserInteraction | undefined) => {
  if (userInteraction === 'SEARCH_HISTORY') return { searchHistory: 'true', ssuggest: undefined };
  if (userInteraction === 'SUGGESTED_QUERY') return { searchHistory: undefined, ssuggest: 'sugg' };
  if (userInteraction === 'SUGGESTED_CATEGORY')
    return { searchHistory: undefined, ssuggest: 'kat' };
  if (userInteraction === 'SUGGESTED_PRODUCT')
    return { searchHistory: undefined, ssuggest: 'prod' };
  return { searchHistory: undefined, ssuggest: undefined };
};

/**
 * Extract the current user interaction type that led to this page from the url query
 */
export const decodeUserInteraction = (query: ParsedUrlQuery): SearchUserInteraction | undefined => {
  if (query.searchHistory === 'true') return 'SEARCH_HISTORY';
  if (query.ssuggest === 'sugg') return 'SUGGESTED_QUERY';
  if (query.ssuggest === 'kat') return 'SUGGESTED_CATEGORY';
  if (query.ssuggest === 'prod') return 'SUGGESTED_PRODUCT';
  return undefined;
};

/**
 * Extract the current promotion banner that led to this page from the url query
 */
export const decodePromotionId = (query: ParsedUrlQuery): string | undefined =>
  typeof query.lmPromo === 'string' ? query.lmPromo : undefined;

/**
 * Decode the marker signifying if the current page was reached from the search history popup
 */
export const decodeSearchHistoryMarker = (query: ParsedUrlQuery): boolean =>
  query.searchHistory === 'true';

/**
 * Encode the information that the current page was reached from the search history popup into the URL query
 */
export const encodeSearchHistoryMarker = (searchHistory: boolean) => ({
  searchHistory: searchHistory ? 'true' : undefined,
});

/**
 * Extract the marker signifying if the current page was reached from a search suggestion
 */
export const decodeSuggestMarker = (
  query: ParsedUrlQuery,
): SearchApiSSuggestParameter | undefined =>
  typeof query.ssuggest === 'string' ? (query.ssuggest as SearchApiSSuggestParameter) : undefined;

/**
 * Encode the information that the current page was reached from a search suggestion into the URL query
 */
export const encodeSuggestMarker = (ssuggest?: SearchApiSSuggestParameter) => ({
  ssuggest,
});

/**
 * Remove all `undefined` values from a query object
 */
export const pruneQuery = (query: ParsedUrlQuery): ParsedUrlQuery =>
  Object.fromEntries(Object.entries(query).filter((kvp) => kvp[1] !== undefined));
